import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { searchContent } from "@/lib/content";
import { getCategoryMeta } from "@/mocks/categories";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => searchContent(keyword), [keyword]);

  useSeo({
    title: keyword ? `“${keyword}”的搜索结果 - 红桃网` : "搜索 - 红桃网",
    description: keyword
      ? `红桃网为你找到与“${keyword}”相关的影视资讯、娱乐新闻、电影推荐与电视剧更新内容。`
      : "在红桃网搜索影视资讯、娱乐新闻、电影推荐与电视剧更新内容。",
    keywords: `${keyword},搜索,影视资讯,娱乐新闻,红桃网`,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed) {
      setParams({ q: trimmed });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 w-full">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-6 md:py-8">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mt-5 mb-4">
              全站搜索
            </h1>
            <form onSubmit={submit} className="flex items-center gap-2 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="输入关键词，搜索影视资讯与娱乐内容"
                  className="w-full pl-11 pr-4 py-3 text-sm rounded-full bg-background-50 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-8">
          {keyword ? (
            <p className="text-sm text-foreground-600 mb-6">
              关于 <span className="text-primary-600 font-semibold">“{keyword}”</span>{" "}
              共找到 <span className="font-semibold">{results.length}</span> 条结果
            </p>
          ) : (
            <p className="text-sm text-foreground-600 mb-6">
              请输入关键词后开始搜索，例如：科幻、综艺、悬疑、古装。
            </p>
          )}

          {keyword && results.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-16 px-4">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-search-eye-line text-3xl text-foreground-400" />
              </span>
              <p className="text-base text-foreground-700 font-medium mb-2">
                没有找到与“{keyword}”相关的内容
              </p>
              <p className="text-sm text-foreground-500 mb-6">
                换个关键词试试，或返回首页浏览推荐内容。
              </p>
              <Link
                to="/"
                className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((item) => {
                const meta = getCategoryMeta(item.category);
                return (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex flex-col sm:flex-row gap-4 rounded-xl bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-full sm:w-44 h-40 sm:h-28 shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={item.cover}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0 flex flex-col">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-600 text-xs font-medium">
                          {meta?.name}
                        </span>
                        <span className="text-xs text-foreground-400 flex items-center gap-1">
                          <i className="ri-calendar-line" /> {item.date}
                        </span>
                      </div>
                      <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mt-1.5">
                        {item.summary}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}